import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { SelfImprovement, Spa } from '@mui/icons-material';
import TrimmedVideo from 'components/play/recovery/TrimmedVideo';

/**
 * OTRIX Play — pantalla de un PASO de práctica guiada (yoga/movilidad/custom): visual del
 * paso (video de la entrenadora > imagen > icono), título/explicación, barra de progreso
 * del paso (scaleX) y qué viene después. Los descansos son un interstitial más calmado.
 */
function GuidedStepScreen({
  step,
  stepElapsed,
  exerciseNumber,
  exerciseTotal,
  nextTitle = null,
  practiceVideo = null
}) {
  const { t } = useTranslation(['play']);
  const isRest = step.type === 'rest';
  const progress = Math.min(1, stepElapsed / Math.max(1, step.duration_seconds));
  const remaining = Math.max(0, step.duration_seconds - Math.floor(stepElapsed));

  // El recorte tiene que viajar CON su video: si el paso trae el suyo manda el del paso, y solo
  // cuando se cae al video general de la práctica se usan las marcas de la práctica. Mezclarlos
  // aplicaría el corte de un archivo sobre otro y dejaría el recuadro en negro.
  const clip = (() => {
    if (step.video_url) {
      return {
        src: step.video_url,
        startMs: step.video_start_ms ?? null,
        endMs: step.video_end_ms ?? null,
        hasAudio: Boolean(step.video_has_audio)
      };
    }
    if (isRest || !practiceVideo?.url) return null;
    return {
      src: practiceVideo.url,
      startMs: practiceVideo.startMs ?? null,
      endMs: practiceVideo.endMs ?? null,
      hasAudio: Boolean(practiceVideo.hasAudio)
    };
  })();

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
      {/* Visual del paso */}
      {!isRest && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 320,
            aspectRatio: '4 / 3',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'hairline.main',
            backgroundColor: 'surface.raised',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          {clip && (
            // Con `video_has_audio` la voz de la entrenadora manda y la música se agacha (el
            // ducking lo aplica el runner); sin audio marcado sigue mudo como hasta ahora.
            <TrimmedVideo
              src={clip.src}
              startMs={clip.startMs}
              endMs={clip.endMs}
              muted={!clip.hasAudio}
              loop
              autoPlay
              sx={{ width: '100%', height: '100%' }}
            />
          )}
          {!clip && step.image_url && (
            <Box component="img" src={step.image_url} alt={step.title} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          {!clip && !step.image_url && <SelfImprovement sx={{ fontSize: 72, color: 'text.disabled' }} />}
        </Box>
      )}

      {isRest && (
        <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
          <Spa sx={{ fontSize: 48, color: 'gold.main' }} />
          <Typography variant="h4" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {remaining}s
          </Typography>
        </Stack>
      )}

      <Stack spacing={0.5} alignItems="center" sx={{ textAlign: 'center', width: '100%' }}>
        {!isRest && (
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.1em', lineHeight: 1 }}>
            {t('recovery.guided.stepOf', { ns: 'play', current: exerciseNumber, total: exerciseTotal, defaultValue: 'Ejercicio {{current}} de {{total}}' })}
          </Typography>
        )}
        <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em">
          {step.title}
        </Typography>
        {step.description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
            {step.description}
          </Typography>
        )}
      </Stack>

      {/* Barra de progreso del paso (solo transform) */}
      <Box sx={{ width: '100%', maxWidth: 360 }}>
        <Box sx={{ height: 5, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: '100%',
              borderRadius: 999,
              backgroundColor: isRest ? 'text.disabled' : 'gold.main',
              transform: `scaleX(${progress})`,
              transformOrigin: 'left',
              transition: 'transform 1s linear'
            }}
          />
        </Box>
        {nextTitle && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 0.75 }}>
            {t('recovery.guided.next', { ns: 'play', title: nextTitle, defaultValue: 'Sigue: {{title}}' })}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

GuidedStepScreen.propTypes = {
  step: PropTypes.object.isRequired,
  stepElapsed: PropTypes.number.isRequired,
  exerciseNumber: PropTypes.number.isRequired,
  exerciseTotal: PropTypes.number.isRequired,
  nextTitle: PropTypes.string,
  /** Video general de la práctica con su recorte: { url, startMs, endMs, hasAudio }. */
  practiceVideo: PropTypes.object
};

export default GuidedStepScreen;
